import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MenuController } from '@ionic/angular';
import type { AccionActividadGrupal } from '../components/formulario-actividad-grupal/formulario-actividad-grupal.component';
import type { ActividadGrupal, CampoDocumentoAsistenciaGrupal } from './actividad-grupal.service';

/** Id del ion-menu usado como panel de actividad grupal (side="end"). */
export const ACTIVIDAD_GRUPAL_PANEL_MENU_ID = 'actividad-grupal-panel';

export type ModoPanelActividadGrupal =
  | AccionActividadGrupal
  | 'ver_detalle'
  | 'subir_asistencia';

@Injectable({ providedIn: 'root' })
export class ActividadGrupalPanelService {
  private readonly menuController = inject(MenuController);
  private readonly _abierto = new BehaviorSubject<boolean>(false);
  private readonly _modo = new BehaviorSubject<ModoPanelActividadGrupal | null>(null);
  private readonly _actividad = new BehaviorSubject<ActividadGrupal | null>(null);
  private readonly _guardado = new Subject<void>();

  /** Emisión cuando el formulario crea/edita correctamente (para recargar lista). */
  readonly guardado$ = this._guardado.asObservable();

  get menuId(): string {
    return ACTIVIDAD_GRUPAL_PANEL_MENU_ID;
  }

  /** True cuando el panel debe renderizarse y habilitarse. */
  getAbierto(): boolean {
    return this._abierto.value;
  }

  getModo(): ModoPanelActividadGrupal | null {
    return this._modo.value;
  }

  /** Solo crear/editar (para el formulario); `null` en modo detalle. */
  getAccion(): AccionActividadGrupal | null {
    const m = this._modo.value;
    if (m === 'crear' || m === 'editar') return m;
    return null;
  }

  getActividad(): ActividadGrupal | null {
    return this._actividad.value;
  }

  /** Reemplaza la actividad del panel (p. ej. tras recargar el listado). */
  actualizarActividad(actividad: ActividadGrupal): void {
    this._actividad.next(actividad);
  }

  /** Quita metadatos de un documento en la actividad abierta (tras eliminar en servidor). */
  quitarDocumentoDeActividad(campo: CampoDocumentoAsistenciaGrupal): void {
    const actividad = this._actividad.value;
    if (!actividad) return;
    const detalle = actividad.detalle ? { ...actividad.detalle } : {};
    delete detalle[campo];
    this._actividad.next({
      ...actividad,
      detalle: Object.keys(detalle).length > 0 ? detalle : null,
    });
  }

  /** Abre el panel (ion-menu). */
  abrir(): void {
    // Compatibilidad: si se llama a `abrir()` sin contexto, asumimos "crear".
    this.abrirCrear();
  }

  abrirCrear(): void {
    this._modo.next('crear');
    this._actividad.next(null);
    this._abierto.next(true);
    // Abrir en el siguiente tick para que Angular actualice [disabled] y el contenido.
    setTimeout(() => {
      this.menuController.open(ACTIVIDAD_GRUPAL_PANEL_MENU_ID);
    }, 0);
  }

  abrirEditar(actividad: ActividadGrupal): void {
    this._modo.next('editar');
    this._actividad.next(actividad);
    this._abierto.next(true);
    setTimeout(() => {
      this.menuController.open(ACTIVIDAD_GRUPAL_PANEL_MENU_ID);
    }, 0);
  }

  abrirVerDetalle(actividad: ActividadGrupal): void {
    this._modo.next('ver_detalle');
    this._actividad.next(actividad);
    this._abierto.next(true);
    setTimeout(() => {
      this.menuController.open(ACTIVIDAD_GRUPAL_PANEL_MENU_ID);
    }, 0);
  }

  abrirSubirAsistencia(actividad: ActividadGrupal): void {
    this._modo.next('subir_asistencia');
    this._actividad.next(actividad);
    this._abierto.next(true);
    setTimeout(() => {
      this.menuController.open(ACTIVIDAD_GRUPAL_PANEL_MENU_ID);
    }, 0);
  }

  /** Cierra el panel. */
  cerrar(): void {
    this.menuController.close(ACTIVIDAD_GRUPAL_PANEL_MENU_ID);
  }

  /** Llamado desde (ionDidClose) del menú para limpiar el estado. */
  alCerrarMenu(): void {
    this._abierto.next(false);
    this._modo.next(null);
    this._actividad.next(null);
  }

  /** Llamado cuando el formulario emite visibleChange(false). */
  onVisibleChange(visible: boolean): void {
    if (!visible) this.cerrar();
  }

  /** Llamado cuando el formulario emite guardado. */
  onGuardado(): void {
    this._guardado.next();
  }
}
